<template>
  <PageShell
    title="LOTLEDGER"
    subtitle="Inventory • Buying • Selling • Reports • Operations"
    :card="false"
    :wide="true"
  >
    <template #logo>
      <img v-if="logoUrl" :src="logoUrl" class="logo" alt="LotLedger" />
    </template>

    <template #actions>
      <router-link class="ll-pill" to="/wizard">Quick Add</router-link>
    </template>

    <main class="menu">
      <section
        v-for="(row, idx) in rows"
        :key="row.key"
        class="row"
        :class="{ open: openKey === row.key }"
        :style="{ animationDelay: `${idx * 80}ms` }"
      >
        <button class="row-header" type="button" @click="toggleRow(row.key)">
          <span class="big-letter">{{ row.letter }}</span>
          <span class="word">{{ row.word }}</span>

          <span class="chev" aria-hidden="true">
            {{ openKey === row.key ? "▾" : "▸" }}
          </span>
        </button>

        <transition name="expand">
          <div v-if="openKey === row.key" class="row-body">
            <div class="option-grid">
              <router-link
                v-for="opt in row.options"
                :key="opt.label"
                class="option"
                :to="opt.to"
              >
                <div class="opt-title">{{ opt.label }}</div>
                <div v-if="opt.hint" class="opt-hint">{{ opt.hint }}</div>
              </router-link>
            </div>
          </div>
        </transition>
      </section>
    </main>
  </PageShell>
</template>

<script>
import PageShell from "@/components/layout/PageShell.vue";

export default {
  name: "DashboardPage",
  components: { PageShell },
  data() {
    return {
      openKey: null,
      logoUrl: null,
      rows: [
        {
          key: "add",
          letter: "A",
          word: "Add",
          options: [
            { label: "Add Inventory", to: "/add-inventory", hint: "Manual entry, imports, assistant" },
            { label: "Expenses", to: "/expenses", hint: "Record operating expenses" },
          ],
        },
        {
          key: "manage",
          letter: "M",
          word: "Manage",
          options: [
            { label: "Inventory", to: "/inventory", hint: "On-hand, cost, last updated" },
            { label: "Items", to: "/items", hint: "Item master list" },
          ],
        },
        {
          key: "sell",
          letter: "S",
          word: "Sell",
          options: [
            { label: "Sales", to: "/sales", hint: "Create sales, ship, track profit" },
          ],
        },
        {
          key: "reports",
          letter: "R",
          word: "Reports",
          options: [
            { label: "Reports Dashboard", to: "/reports-dashboard", hint: "All reports in one place" },
            { label: "Profit & Loss", to: "/reports/profit-loss", hint: "By month, by category" },
            { label: "Inventory Value", to: "/reports/inventory-value", hint: "Current inventory valuation" },
          ],
        },
        {
          key: "settings",
          letter: "S",
          word: "Settings",
          options: [
            { label: "System", to: "/system", hint: "Users, tenant, configuration" },
            { label: "Platforms", to: "/platforms", hint: "Marketplace integrations" },
            { label: "Licenses / Login", to: "/licenses", hint: "Auth / account access" },
          ],
        },
      ],
    };
  },
  methods: {
    toggleRow(key) {
      this.openKey = this.openKey === key ? null : key;
    },
  },
};
</script>

<style scoped>
.logo {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  object-fit: contain;
  border: 1px solid rgba(131, 167, 203, 0.8);
  background: #ffffff;
}

/* Your existing menu styles (kept) */
.menu {
  display: grid;
  gap: 12px;
}

.row {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(131, 167, 203, 0.85);
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.06);
  animation: fadeUp 420ms ease both;
}

.row-header {
  width: 100%;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
}

.big-letter {
  width: 40px;
  height: 40px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 18px;
  color: white;
  background: #288671;
  box-shadow: 0px 6px 14px rgba(40, 134, 113, 0.25);
}

.word {
  font-size: 18px;
  font-weight: 900;
  letter-spacing: 0.5px;
}

.chev {
  margin-left: auto;
  font-size: 18px;
  opacity: 0.75;
  font-weight: 900;
}

.row-body {
  padding: 0 16px 16px 16px;
}

.option-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(180px, 1fr));
  gap: 10px;
}

.option {
  text-decoration: none;
  border-radius: 14px;
  border: 1px solid rgba(176, 196, 222, 0.85);
  background: #f6fbff;
  padding: 12px;
  transition: transform 120ms ease, box-shadow 120ms ease;
  color: #1f2a35;
}

.option:hover {
  transform: translateY(-2px);
  box-shadow: 0px 10px 18px rgba(0, 0, 0, 0.08);
}

.opt-title {
  font-weight: 900;
  font-size: 14px;
}

.opt-hint {
  margin-top: 4px;
  font-size: 12px;
  opacity: 0.75;
  font-weight: 600;
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Collapse animation */
.expand-enter-active,
.expand-leave-active {
  transition: max-height 220ms ease, opacity 220ms ease;
}
.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}
.expand-enter-to,
.expand-leave-from {
  max-height: 400px;
  opacity: 1;
}

@media (max-width: 980px) {
  .option-grid {
    grid-template-columns: 1fr;
  }
}
</style>
